import {
  FormControl,
  FormErrorMessage,
  FormHelperText,
  Select,
  SelectProps
} from '@chakra-ui/react'
import { ReactNode } from 'react'
import { FieldValues, useFormContext } from 'react-hook-form'
import { AttentionIcon } from '../Icons/AttentionIcon'
import { CommonFormLabel } from './CommonFormLabel'
import { FieldInputProps, getErrorMessage } from './FormTextInput'

type FormSelectProps<T extends FieldValues, S> = FieldInputProps<T, S> &
  SelectProps & {
    children?: ReactNode | undefined
    valueAsNumber?: boolean
  }

export const FormSelect = <
  T extends FieldValues = never,
  S extends string | number = string
>({
  name,
  isRequired,
  label,
  helperText,
  children,
  w,
  labelIndexNumber,
  helpTooltipText,
  errorMessage,
  onChange,
  valueAsNumber,
  ...inputProps
}: FormSelectProps<T, S>): ReturnType<React.VFC> => {
  const { register, watch } = useFormContext()

  const _errorMessage = getErrorMessage(name) || errorMessage
  const color =
    watch(name) == null || watch(name) === '' ? 'gray.400' : 'primary.black'
  return (
    <FormControl isRequired={isRequired} isInvalid={_errorMessage} w="auto">
      <CommonFormLabel
        label={label}
        labelIndexNumber={labelIndexNumber}
        helpTooltipText={helpTooltipText}
        isRequired={isRequired}
      />
      <Select
        {...register(name, { onChange, valueAsNumber })}
        w={w}
        color={color}
        {...inputProps}
        required={false}
      >
        {children}
      </Select>
      <FormErrorMessage>
        <AttentionIcon />
        {_errorMessage}
      </FormErrorMessage>
      {helperText && <FormHelperText>{helperText}</FormHelperText>}
    </FormControl>
  )
}
